import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import BrandKitAudiences from './BrandKitAudiences';
import BrandKitColors from './BrandKitColors';
import BrandKitFonts from './BrandKitFonts';
import BrandKitGoals from './BrandKitGoals';
import BrandKitLinks from './BrandKitLinks';
import BrandKitMediaSection from './BrandKitMediaSection';
import BrandKitPullQuotes from './BrandKitPullQuotes';
import BrandKitTextareaSection from './BrandKitTextareaSection';
import BrandKitVideoTemplates from './BrandKitVideoTemplates';
import BrandKitWebsite from './BrandKitWebsite';
import ShowcaseLanguageSettings from './ShowcaseLanguageSettings';
import useLoadBrandKitFonts from './utils/useLoadBrandKitFonts';

/** ~1s at 60fps — long enough for the sections to mount, short enough that a
 *  genuinely absent hash target stops costing frames. */
const HASH_SCROLL_MAX_FRAMES = 60;

const BrandKit = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { enableClipProducerVideoTemplateSelection } = useFlagsCombination(
    userIdentity.currentRole,
  );
  useLoadBrandKitFonts(videoCreator);
  useVideoCreatorDataInitialization();

  // Land on the section named by the URL hash (e.g. `/brand-kit#languages` from
  // the glossary import's "Add"). The sidebar's own in-page anchors are plain
  // `href="#id"` links the browser handles; a client-side navigation carrying a
  // hash needs this. The target section can mount a few frames later (showcase
  // data still loading), so retry across a short window instead of scrolling
  // once against a DOM that doesn't have it yet.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    let frame = 0;
    let attempts = 0;
    const tryScroll = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView();
        return;
      }
      if (attempts++ < HASH_SCROLL_MAX_FRAMES) {
        frame = requestAnimationFrame(tryScroll);
      }
    };
    tryScroll();
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return (
    <Main key={videoCreator.organization?.id}>
      <div className="container">
        <div className="topbar">
          <h1>Brand Kit</h1>
        </div>

        <div className="sidebar">
          {SIDEBAR_ITEMS.filter(item => {
            if (
              item.id === 'video-templates' &&
              !enableClipProducerVideoTemplateSelection
            )
              return false;
            return true;
          }).map(item => (
            <a href={`#${item.id}`} key={item.id}>
              {item.title}
            </a>
          ))}
        </div>

        <div className="main">
          <BrandKitWebsite />
          <BrandKitMediaSection property="organizationLogos" />
          <BrandKitColors type="primary" />
          <BrandKitColors type="secondary" />
          <BrandKitFonts />
          <BrandKitGoals />
          <BrandKitAudiences />
          <BrandKitMediaSection property="branding.designElements" />
          {enableClipProducerVideoTemplateSelection && (
            <BrandKitVideoTemplates />
          )}
          <BrandKitPullQuotes />
          <BrandKitMediaSection property="organizationArtifacts" />
          <BrandKitMediaSection property="organizationArtifactsVideo" />
          <ShowcaseLanguageSettings />
          <BrandKitTextareaSection property="brandVoice" />
          <BrandKitTextareaSection property="avoidWords" />
          <BrandKitTextareaSection property="customVocabulary" />
          <BrandKitLinks />
        </div>
      </div>
    </Main>
  );
});

const SIDEBAR_ITEMS = [
  { title: 'Organization Website', id: 'org-website' },
  { title: 'Logos', id: 'logos' },
  { title: 'Primary Colors', id: 'primary-colors' },
  { title: 'Secondary Colors', id: 'secondary-colors' },
  { title: 'Fonts', id: 'fonts' },
  { title: 'Goals', id: 'brand-goals' },
  { title: 'Audiences', id: 'brand-audiences' },
  { title: 'Design Elements', id: 'design-elements' },
  { title: 'Video Templates', id: 'video-templates' },
  { title: 'Pull Quotes', id: 'pull-quotes' },
  { title: 'Photos', id: 'photos' },
  { title: 'Videos', id: 'videos' },
  { title: 'Languages', id: 'languages' },
  { title: 'Brand Voice', id: 'brand-voice' },
  { title: 'Avoid Words', id: 'avoid-words' },
  { title: 'Company Dictionary', id: 'company-dictionary' },
  { title: 'External Links', id: 'external-links' },
];

const Main = styled.main`
  min-height: calc(100vh - 69px);
  display: flex;
  flex-direction: column;

  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  .container {
    flex-grow: 1;
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 40px 20px;
    display: grid;
    grid-template-columns: 288px 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'topbar topbar'
      'sidebar main';
    grid-column-gap: 24px;
  }

  .topbar {
    grid-area: topbar;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 8px;

    > h1 {
      font-size: 32px;
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
      margin: 0;
    }
  }

  .sidebar {
    grid-area: sidebar;
    align-self: flex-start;
    position: sticky;
    top: 8px;
    display: flex;
    flex-direction: column;
    padding: 12px 0;
    gap: 0;

    > a {
      display: block;
      text-decoration: none;
      border-radius: 4px;
      color: var(--foreground);
      padding: 6px 12px;
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
      transition: var(--transition-normal);
      &:hover {
        background-color: var(--surface-elevated);
      }
    }
  }

  .main {
    grid-area: main;
    display: flex;
    flex-direction: column;
    gap: 16px;
    > * {
      padding: 24px 0;
    }
    > *:not(:last-child) {
      border-bottom: 1px solid var(--border);
    }
  }
`;

export default BrandKit;
